import React, { useEffect, useState } from 'react';
import { Clock, Sparkle } from '@phosphor-icons/react';

const fmt = (ms) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

/**
 * Vague life-clock that shows a phase, not exact countdown — keeps the
 * emotional weight. Phases match server mood_hint thresholds.
 */
const LifeClock = ({ bubble }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  if (!bubble?.expires_at || !bubble?.created_at) return null;
  const start = new Date(bubble.created_at).getTime();
  const end = new Date(bubble.expires_at).getTime();
  const total = end - start;
  const elapsed = Math.max(0, now - start);
  const remaining = Math.max(0, end - now);
  const ratio = Math.min(1, elapsed / total);

  let phase = 'Vibrant';
  let color = 'text-bx-cyan';
  let bar = 'from-bx-cyan to-bx-volt';
  if (ratio > 0.95) { phase = 'Final journey'; color = 'text-bx-magenta'; bar = 'from-bx-magenta to-bx-legendary'; }
  else if (ratio > 0.85) { phase = 'Restless'; color = 'text-bx-legendary'; bar = 'from-bx-legendary to-bx-magenta'; }
  else if (ratio > 0.65) { phase = 'Tired'; color = 'text-bx-gold'; bar = 'from-bx-gold to-bx-legendary'; }

  return (
    <div className="glass rounded-xl p-4">
      <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-white/60">
        <span className="flex items-center gap-1"><Clock size={12} weight="bold" /> Life Clock</span>
        <span>{bubble.bubble_class}-class · ~{bubble.lifespan_days}d lifespan</span>
      </div>
      <div className={`font-display text-2xl font-black mt-2 ${color}`}>{phase}</div>
      {bubble.mood_hint && (
        <div className="text-sm text-white/70 mt-1 italic">"{bubble.mood_hint}"</div>
      )}
      <div className="mt-3 h-2 rounded-full bg-white/5 overflow-hidden">
        <div className={`h-full bg-gradient-to-r ${bar} transition-[width] duration-700`}
             style={{ width: `${ratio * 100}%` }} />
      </div>
      <div className="mt-2 flex justify-between text-[10px] uppercase tracking-widest text-white/60">
        <span>Age: {fmt(elapsed)}</span>
        <span className={remaining < 86400000 ? 'text-bx-magenta' : ''}>
          {remaining > 0 ? `~${fmt(remaining)} left` : 'fading…'}
        </span>
      </div>
    </div>
  );
};

export const VoiceButton = ({ catchphrase, name }) => {
  const [playing, setPlaying] = useState(false);
  const speak = () => {
    if (!('speechSynthesis' in window) || !catchphrase) return;
    window.speechSynthesis.cancel();
    const utter = new SpeechSynthesisUtterance(catchphrase);
    const voices = window.speechSynthesis.getVoices();
    // Try to pick a distinct voice based on name hash
    if (voices.length) {
      const idx = Array.from(name || '').reduce((a, c) => a + c.charCodeAt(0), 0) % voices.length;
      utter.voice = voices[idx];
    }
    utter.pitch = 1.1; utter.rate = 1.05;
    utter.onstart = () => setPlaying(true);
    utter.onend = () => setPlaying(false);
    window.speechSynthesis.speak(utter);
  };
  if (!catchphrase) return null;
  return (
    <button
      data-testid="bubble-voice-btn"
      onClick={speak}
      className="px-4 py-2 rounded-full glass-hi text-xs font-bold flex items-center gap-1.5 hover:bg-white/10 transition"
    >
      <Sparkle size={14} weight={playing ? 'fill' : 'bold'} className={playing ? 'text-bx-volt animate-pulse' : ''} />
      {playing ? 'speaking…' : 'hear voice'}
    </button>
  );
};

export default LifeClock;
